import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useValidRouteUrl } from '@/shared/hooks/useValidRouteUrl';
import { useBreadcrumbs, BreadcrumbItem } from '@/shared/hooks/useBreadcrumbs';
import Tooltip from '@/shared/components/Tooltip';

interface BreadcrumbProps {
  /** Explicit breadcrumbs. If omitted, auto-generated from the current URL. */
  breadcrumbs?: BreadcrumbItem[];
}

/**
 * Shared breadcrumb used by every details page.
 *
 * Design choices (ECOPD refresh):
 *   - A small Home glyph anchors the first crumb so the root of the trail
 *     is always recognizable at a glance.
 *   - Inline text links (no pills / no underline) — hover recolors to
 *     primary and the *previous* arrow nudges 2px to the right, reading
 *     as "follow me this way". Snappy but never loud.
 *   - Separators are a `ChevronsRight` (»), slightly larger than body
 *     text so they read as "path connectors" rather than dividers — and
 *     they still pick up the hover nudge from the preceding link.
 *   - The current page sits flush with its siblings in `text-text` +
 *     `font-semibold`, with a short primary accent bar underneath —
 *     identifies "you are here" without the bordered-pill look that
 *     made it feel like a tab strip.
 */
const Breadcrumb: React.FC<BreadcrumbProps> = ({ breadcrumbs: propCrumbs }) => {
  const isValidRouteUrl = useValidRouteUrl();
  const autoCrumbs = useBreadcrumbs();
  const breadcrumbs = propCrumbs ?? autoCrumbs;

  return (
    <nav
      aria-label="Breadcrumb"
      className="text-sm font-normal mb-2 flex items-center flex-wrap gap-y-1"
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const asLink = !!(crumb.url && isValidRouteUrl(crumb.url)) && !isLast;
        // Only show tooltip on link items (items with a url that are not the current page)
        const labelNode = (crumb.tooltip && asLink) ? (
          <Tooltip text={crumb.tooltip} position="bottom" align="left" usePortal>
            <span className="truncate max-w-[18rem]">{crumb.label}</span>
          </Tooltip>
        ) : (
          <span className="truncate max-w-[18rem]">{crumb.label}</span>
        );

        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <ChevronRight
                className="w-3.5 h-3.5 mx-1 text-text-muted/60 flex-shrink-0"
                aria-hidden="true"
              />
            )}
            {asLink ? (
              <Link
                to={crumb.url!}
                className="inline-flex items-center gap-1 text-breadcrumbLink hover:text-breadcrumbLink/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breadcrumbLink/40 rounded-sm"
              >
                {labelNode}
              </Link>
            ) : isLast ? (
              <span
                className="inline-flex items-center gap-1 text-breadcrumbLink font-medium underline underline-offset-2"
                aria-current="page"
              >
                {labelNode}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-text-muted">
                {labelNode}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default React.memo(Breadcrumb);
